import React, {useEffect, useState} from 'react';
import {Route, Router, Switch, withRouter} from 'react-router-dom';
import {connect} from 'react-redux';
import moment from 'moment';
import 'moment-timezone';
import {PrivateRoute} from '../shared/components/PrivateRoute';
import history from '../history';
import {Login, SplashPage, SetPassword, SaveProfile, AgreeToTerms} from './index';
import {Header} from '../shared';
import debug from 'debug';
import {
  _closeSession,
  _openModal,
  _setNotificationModalOpenTimestamp,
  _setAggregationStatus,
  isEntitled,
} from '../shared/store';
import PropTypes from 'prop-types';

import portfolio from './sections/portfolio/routes';
import accounts from './sections/accounts/routes';
import groups from './sections/groups/routes';
import documents from './sections/documents/routes';
import PasswordReset from '../shared/pages/PasswordReset';
import About from './pages/About';
import PrefPage from './shared/preferences/PrefPage';
import Profile from './pages/Profile';
import Welcome from './pages/Welcome';
import axiosInstance from '../shared/utils/axios';
import Advisors from './pages/Advisors';
import {calculateAggregationStatus} from '../shared/utils/utils';

const log = debug('circleblack:routes');

function Routes(props) {
  const {location, logout, session, openModal, setAggregationStatus} = props;

  //const [windowSize, setWindowSize] = useState('');
  const [lastResize, setLastResize] = useState(moment(0));

  log('rendering for', location.pathname);
  const logo = (session && session.profile) && session.profile.logo
    || (session.profile.branch && session.profile.branch.logo)
    || (session.profile.firm && session.profile.firm.logo)
    || 'https://media.circleblack.com/cb/CB_Master_Logo_Tag_Reverse.svg';

  const sections = [
    ...portfolio.sections(),
    ...accounts.sections(),
    ...groups.sections(),
    ...documents.sections(),
  ].filter(s => !!s);

  // ToDo: delete this and import from cssUtils when breakpoints are finalized.
  /*const getBreakpoint = (sectionLength = 5) => {
    let breakpoint = 841;
    if (sectionLength > 5) {
      breakpoint += ((sectionLength - 5) * 110);
    }
    return breakpoint;
  };*/

  const handleWindowSize = () => {
    /*if (window.innerWidth >= getBreakpoint(sections.length)) {
      setWindowSize('md');
    } else {
      setWindowSize('sm');
    }*/
    setLastResize(moment());
  };

  const handleAggregationAlert = (isInitialLogin) => {
    axiosInstance.get('/user/institutions')
      .then(({data: institutions}) => {
        const aggregationStatus = calculateAggregationStatus(institutions);
        const isWelcomePageHidden = session.profile.hideWelcome;
        const hasAuthIssues = aggregationStatus === 'AUTH_ISSUES';

        setAggregationStatus(aggregationStatus);

        if (isInitialLogin && hasAuthIssues && isWelcomePageHidden) {
          openModal({type: 'AggregationAlertModal'});
        }
      });
  };

  const handleInitialLoad = () => {
    const initialLoad = JSON.parse(localStorage.getItem('initialLoad'));

    if (initialLoad) {
      localStorage.setItem('initialLoad', JSON.stringify(false));
    }

    handleAggregationAlert(initialLoad);
  };

  useEffect(() => {
    window.addEventListener('resize', handleWindowSize);
    return function cleanup() {
      window.removeEventListener('resize', handleWindowSize);
    };
  });

  /*useEffect(() => {
    if (window.innerWidth >= getBreakpoint(sections.length)) {
      setWindowSize('md');
    } else {
      setWindowSize('sm');
    }
  }, []);*/

  useEffect(() => {
    const isLoggedIn = session.profile.id && !session.pendingMFA;

    if (!isLoggedIn) {
      return;
    }

    handleInitialLoad();
  }, [session.profile.id, session.pendingMFA]);

  const icons = [
    ['AUTH_ISSUES', 'AGGREGATION_ISSUES'].includes(session.aggregationStatus) && {
      image: 'attention',
      color: '#FFC003',
      width: '20px',
      height: '20px',
      margin: '10px',
      popover: {text: 'There is an issue retrieving some of the data in your portfolio. Click the icon to view your accounts.'},
      action: () => history.push('/accounts'),
    },
    // {
    //   image: 'notifications',
    //   action: () => {
    //     setNotificationModalOpenTimestamp(moment());
    //     openModal({type: 'Notifications'});
    //   },
    // },
  ].filter(o => !!o);

  const iconMenuOptions = [
    /*windowSize === 'sm' && {
      icon: 'notifications',
      label: 'Notifications',
      action: () => {
        setNotificationModalOpenTimestamp(moment());
        openModal({type: 'Notifications'});
      },
      lastResize: lastResize,
    },*/
    {
      icon: 'client',
      label: 'Profile',
      action: () => history.push('/profile'),
      value: '/profile',
    },
    {
      icon: 'advisors',
      label: 'My Advisors',
      action: () => history.push('/advisors'),
      value: '/advisors',
    },
    {
      icon: 'preferences',
      label: 'Preferences',
      action: () => history.push('/preferences'),
      value: '/preferences',
    },
    {
      icon: 'feedback',
      label: 'About',
      action: () => history.push('/about'),
      value: '/about',
    },
    isEntitled([{group: 'partners', feature: 'zendesk', access: 'r'}]) && {
      icon: 'questionMarkCircle',
      label: 'Help Center',
      action: () => axiosInstance('/auth/zendesk/login')
        .then(res => window.open(res.data.value, '_blank'))
        .catch(err => openModal({type: 'Error', title: 'Help Center Error', err})),
    },
    !isEntitled([{group: 'advisor', feature: 'ssoOnly', access: 'r'}]) && {
      icon: 'logout',
      id: 'logout',
      label: 'Logout',
      isDisabled: !!session.runAsInvestor,
      action: () => {
        logout();
      },
    },
  ].filter(o => !!o);

  return <Router history={history}>
    <Switch>
      <Route path={'/login'} component={Login} exact/>
      <Route path={'/firstLogin/setPassword'} component={SetPassword} exact/>
      <Route path={'/firstLogin/profile'} component={SaveProfile} exact/>
      <Route path={'/firstLogin/terms'} component={AgreeToTerms} exact/>
      <Route path={'/user/password/reset/:token'} exact component={PasswordReset}/>
      <Route render={() => <SplashPage>
        {props.session.entitlements && <Header logo={logo}
                                               platformIcon={session?.profile?.brand?.investorIcon}
                                               sections={sections}
                                               icons={icons}
                                               menuRoutes={<Switch>
                                                 <PrivateRoute path='/about' component={About}/>
                                                 <PrivateRoute path='/profile' component={Profile}/>
                                                 <PrivateRoute path='/preferences' component={PrefPage}/>
                                                 <PrivateRoute path='/welcome' component={Welcome}/>
                                                 <PrivateRoute path='/advisors' component={Advisors}/>
                                               </Switch>}
                                               iconMenuOptions={{
                                                 mainSection: {
                                                   options: iconMenuOptions,
                                                 },
                                               }}
                                               lastResize={lastResize}/>}
      </SplashPage>}/>
    </Switch>
  </Router>;
}

const mapStateToProps = state => ({
  session: state.session,
  reportTableReset: state.badges.reportTableReset,
  tableKey: state.badges.tableKey,
  rerenderToggle: state.badges.rerenderToggle,
});

const mapDispatchToProps = dispatch => ({
  logout: () => {
    dispatch(_closeSession());
  },
  openModal: modal => dispatch(_openModal(modal)),
  setNotificationModalOpenTimestamp: timestamp => dispatch(_setNotificationModalOpenTimestamp(timestamp)),
  setAggregationStatus: (status) => dispatch(_setAggregationStatus(status)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Routes));

Routes.propTypes = {
  /**
   * The location object provided by the Router
   */
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  /**
   * The logout function that dispatches _closeSession from Redux
   */
  logout: PropTypes.func,
  /**
   * Are from reducers
   */
  openModal: PropTypes.func.isRequired,
  session: PropTypes.shape().isRequired,
  setNotificationModalOpenTimestamp: PropTypes.func,
  setReportPageVisitTimestamp: PropTypes.func,
  setCompliancePageVisitTimestamp: PropTypes.func,
  rerenderToggle: PropTypes.bool,
  setAggregationStatus: PropTypes.func,
};
